// contactSales — builds a `mailto:` href with a ready-to-send draft addressed to
// the CharityStack sales/account inbox.
//
// WHY A mailto: the dashboard has no in-app support widget, so the established
// pattern (BillingPlansPage "Talk to sales") is a prefilled mailto — zero
// backend, opens the user's own mail client. This module centralizes that
// pattern so the billing page and the PE-1678 "Contact us" affordances share one
// source (subject/body can't drift between surfaces).
//
// The draft is written from the ORG's perspective and personalized with the org
// name + current plan when available, so a busy admin can fire it off in one
// click yet it still reads bespoke. Body is plain text with CRLF (\r\n) per the
// mailto/email convention, and the whole subject/body is URL-encoded so it
// survives the mailto: handoff intact.

// The inbox every contact draft routes to — the SINGLE source for the contact
// address across the dashboard: the billing "Talk to sales" strip, the billing
// managed-account "Contact us" (PE-1690), and the marketing broadcasts/segments
// "Contact us" (PE-1678) all build their mailto through the helpers below, so
// changing this one constant repoints every contact CTA at once.
const CONTACT_SALES_EMAIL = 'hello@charitystack.com';

export interface ContactSalesOptions {
  /** Org display name (merchant.merchantName). Optional — drafts degrade gracefully without it. */
  orgName?: string | null;
  /** The org's current plan name, for context in the body. Optional. */
  currentPlanName?: string | null;
  /**
   * Display name of the plan the admin clicked (e.g. "Plus"). Optional —
   * when present, the draft names the tier so the account team knows which
   * change is being requested; without it the draft stays generic.
   */
  targetPlanName?: string | null;
}

function encodeMailto(subject: string, body: string): string {
  return `mailto:${CONTACT_SALES_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

/**
 * "Talk to sales" — a Pro-upgrade-interest draft (the billing page's
 * "Scaling past Plus?" strip). Preserves the exact copy that shipped on
 * BillingPlansPage so this extraction is behavior-preserving.
 */
export function buildTalkToSalesMailto({
  orgName,
  currentPlanName,
}: ContactSalesOptions): string {
  const org = orgName?.trim();
  const planName = currentPlanName ?? 'current';

  const subject = org
    ? `Interested in CharityStack Pro — ${org}`
    : 'Interested in CharityStack Pro';

  const intro = org
    ? `Hi CharityStack team,\r\n\r\nI'm reaching out on behalf of ${org}. We're currently on the ${planName} plan and looking at upgrading to Pro.`
    : `Hi CharityStack team,\r\n\r\nWe're currently on the ${planName} plan and looking at upgrading to Pro.`;

  const body = [
    intro,
    '',
    'Could you share Pro pricing for our volume and a good time for a quick call this week?',
    '',
    'Thanks,',
    org ? `${org} team` : '',
  ].join('\r\n');

  return encodeMailto(subject, body);
}

/**
 * "Contact us" — the PE-1678 managed-account draft, used by the marketing upsell
 * (broadcasts/segments) and (optionally) a billing-screen panel for `legacy_custom`
 * orgs. These merchants are on a manually-negotiated custom plan, so the draft
 * frames the ask as a plan/marketing conversation with their account team — NOT a
 * self-serve upgrade.
 */
/**
 * Plan-change request from a PRO org (PE-1851). Pro is sales-managed, so the
 * billing page's plan-change CTAs open this draft instead of self-serve
 * checkout/portal routing. Distinct from buildContactUsMailto: that draft is
 * the legacy_custom MARKETING ask ("enable broadcasts and segments"), which
 * reads wrong for a Pro org asking to change its plan.
 */
export function buildProPlanChangeMailto({
  orgName,
  targetPlanName,
}: ContactSalesOptions): string {
  const org = orgName?.trim();
  const target = targetPlanName?.trim();

  // Name the requested tier when the draft comes from a specific plan card,
  // so the account team knows which change is being asked for; the generic
  // "Contact us" affordances (no card clicked) keep the open-ended ask.
  const subject = target
    ? org
      ? `Plan change request (Pro → ${target}) — ${org}`
      : `Plan change request (Pro → ${target})`
    : org
      ? `Plan change request — ${org}`
      : 'Plan change request';

  const ask = target
    ? `We're on the Pro plan and would like to talk about moving to the ${target} plan.`
    : `We're on the Pro plan and would like to talk about changing our plan.`;

  const intro = org
    ? `Hi CharityStack team,\r\n\r\nI'm reaching out on behalf of ${org}. ${ask}`
    : `Hi CharityStack team,\r\n\r\n${ask}`;

  const body = [
    intro,
    '',
    'Could you let us know the options and a good time for a quick call this week?',
    '',
    'Thanks,',
    org ? `${org} team` : '',
  ].join('\r\n');

  return encodeMailto(subject, body);
}

export function buildContactUsMailto({ orgName }: ContactSalesOptions): string {
  const org = orgName?.trim();

  const subject = org
    ? `Question about our CharityStack plan — ${org}`
    : 'Question about our CharityStack plan';

  const intro = org
    ? `Hi CharityStack team,\r\n\r\nI'm reaching out on behalf of ${org}. We're on a custom plan managed by your team, and I'd like to talk about marketing — broadcasts and segments — for our account.`
    : `Hi CharityStack team,\r\n\r\nWe're on a custom plan managed by your team, and I'd like to talk about marketing — broadcasts and segments — for our account.`;

  const body = [
    intro,
    '',
    "Could you let us know what's involved in enabling it, and a good time for a quick call this week?",
    '',
    'Thanks,',
    org ? `${org} team` : '',
  ].join('\r\n');

  return encodeMailto(subject, body);
}
